import { useEffect } from 'react';
import { pushApi, type QuietHoursView } from '../api/client';
import { openInApp } from '../pwa/pwa';
import { useMessagesLive, type LiveEvent } from './messages';
// A text for your ring group or queue (1.12.33): its own sound, the spoken line, reminders.
import { spokenLine } from './teamSound';
import { armTeamSound, teamConversationChanged, teamPresence, teamTextAlert, type AlertEnv } from './teamSoundPlayer';

/**
 * A new message, in the Stitch that's open (any tab, the installed app): Stitch's own sound, and
 * while Stitch isn't in front, a notification shown by the page itself. The server says when (an
 * 'alert' on the live stream: a message this person would be told about, as they chose, and not on
 * do not disturb). Nothing for the conversation they're reading. One tab does it, not every tab.
 * A push for the same message (sent a minute on, if still unread) replaces the notification
 * quietly (same tag and address; sw.ts).
 */

// --- the sound ----------------------------------------------------------------------------------

/**
 * Stitch's message sound: two quick drops, the second higher and softer. Every wallboard sound is
 * a steady tone; this one slides down, so the two are never mixed up.
 */
const DROPS: { from: number; to: number; t: number; d: number; g: number }[] = [
  { from: 1568, to: 784, t: 0, d: 0.17, g: 0.9 },
  { from: 2349, to: 1175, t: 0.085, d: 0.15, g: 0.5 }
];
const VOLUME = 0.45;

type AudioCtor = typeof AudioContext;
let ctx: AudioContext | null = null;
function context(): AudioContext | null {
  if (ctx) return ctx;
  if (typeof window === 'undefined') return null;
  const Ctor = (window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioCtor }).webkitAudioContext) as AudioCtor | undefined;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    ctx = null;
  }
  return ctx;
}

let armed = false;
/** Browsers start sound only after the person clicks or types in the page: be ready from the first time. */
function armSound(): void {
  if (armed || typeof window === 'undefined') return;
  armed = true;
  const wake = () => {
    const c = context();
    if (c && c.state !== 'running') void c.resume().catch(() => undefined);
  };
  for (const e of ['pointerdown', 'keydown', 'touchend'] as const) window.addEventListener(e, wake, { passive: true });
}

let lastPlayed = 0;
/** Plays the message sound (a burst of messages makes one sound). False when the browser won't yet. */
export function playMessageSound(): boolean {
  const c = context();
  if (!c || c.state !== 'running') return false;
  if (Date.now() - lastPlayed < 900) return true;
  lastPlayed = Date.now();
  const master = c.createGain();
  master.gain.value = VOLUME;
  master.connect(c.destination);
  const start = c.currentTime + 0.02;
  for (const n of DROPS) {
    const osc = c.createOscillator();
    const g = c.createGain();
    const t0 = start + n.t;
    osc.type = 'sine';
    osc.frequency.setValueAtTime(n.from, t0);
    osc.frequency.exponentialRampToValueAtTime(n.to, t0 + n.d * 0.55);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(n.g, t0 + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + n.d);
    osc.connect(g).connect(master);
    osc.start(t0);
    osc.stop(t0 + n.d + 0.03);
  }
  return true;
}

/** From a click (Your settings → "Play"): the sound once. */
export async function previewMessageSound(): Promise<void> {
  const c = context();
  if (c && c.state !== 'running') await c.resume().catch(() => undefined);
  lastPlayed = 0;
  playMessageSound();
}

// --- this device's choice -------------------------------------------------------------------------

const SOUND_KEY = 'stitch.sound.messages';
/** The message sound on this device (on unless turned off here). */
export function soundOn(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) !== 'off';
  } catch {
    return true;
  }
}
export function setSoundOn(on: boolean): void {
  try {
    if (on) localStorage.removeItem(SOUND_KEY);
    else localStorage.setItem(SOUND_KEY, 'off');
  } catch {
    // Private browsing: it lasts until the page closes.
  }
}

// --- their notification settings (on, previews, quiet hours), for what the page shows ----------------

interface Prefs {
  enabled: boolean;
  previews: boolean;
  quiet: QuietHoursView | null;
}
let prefs: Prefs | null = null;
let prefsAt = 0;
async function currentPrefs(): Promise<Prefs> {
  if (prefs && Date.now() - prefsAt < 5 * 60_000) return prefs;
  try {
    const r = await pushApi.settings();
    prefs = { enabled: r.enabled, previews: r.previews, quiet: r.quiet };
    prefsAt = Date.now();
  } catch {
    prefs ??= { enabled: true, previews: true, quiet: null };
  }
  return prefs;
}
/** After Your settings → Notifications changed: read them again next time. */
export function forgetAlertPrefs(): void {
  prefs = null;
}

/** Within quiet hours, as the server reads them (push/service.ts). */
export function inQuietHours(q: QuietHoursView | null, now = new Date()): boolean {
  if (!q) return false;
  const mins = (hhmm: string) => {
    const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
    return m ? Number(m[1]) * 60 + Number(m[2]) : NaN;
  };
  const from = mins(q.from);
  const to = mins(q.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || from === to) return false;
  let t: number;
  try {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: q.zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(now);
    t = Number(parts.find((x) => x.type === 'hour')?.value) * 60 + Number(parts.find((x) => x.type === 'minute')?.value);
  } catch {
    return false;
  }
  return from < to ? t >= from && t < to : t >= from || t < to;
}

// --- one tab, not every tab -------------------------------------------------------------------------

/** The first tab to ask gets it (and keeps it a few seconds, so the others find it taken). */
function claim(name: string): Promise<boolean> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  if (!locks?.request) return Promise.resolve(true);
  return new Promise((resolve) => {
    locks
      .request(name, { ifAvailable: true }, (lock) => {
        if (!lock) {
          resolve(false);
          return undefined;
        }
        resolve(true);
        return new Promise<void>((r) => window.setTimeout(r, 5000));
      })
      .catch(() => resolve(true));
  });
}

// --- notifications the page shows -----------------------------------------------------------------

type Alert = NonNullable<LiveEvent['alert']>;
const ART = { icon: '/icons/icon-192.png', badge: '/icons/badge-96.png' };

async function registration(): Promise<ServiceWorkerRegistration | undefined> {
  try {
    return await navigator.serviceWorker?.getRegistration();
  } catch {
    return undefined;
  }
}

/** Shows a notification for a message, as their settings say. False when it can't or shouldn't. */
async function notify(a: Alert, conversationId: string, p: Prefs, silent: boolean, team?: LiveEvent['team']): Promise<boolean> {
  if (!('Notification' in window) || Notification.permission !== 'granted' || !p.enabled) return false;
  // A team's text keeps "New text for Billing" with previews off: it says nothing of who or what.
  const title = p.previews
    ? a.title
    : team
      ? spokenLine(team)
      : a.kind === 'mention'
        ? 'You were mentioned'
        : a.kind === 'voicemail'
          ? 'New voicemail'
          : a.kind === 'call'
            ? 'Phone call'
            : 'New message';
  const body = p.previews ? a.body : a.kind === 'voicemail' ? 'Open Stitch to listen.' : a.kind === 'call' ? 'Open Stitch to see it.' : 'Open Stitch to read it.';
  const options = { body, tag: a.tag, renotify: true, silent, icon: ART.icon, badge: ART.badge, timestamp: Date.now(), data: { url: a.url, kind: a.kind, ...(conversationId ? { conversationId } : {}) } };
  const reg = await registration();
  try {
    if (reg) {
      await reg.showNotification(title, options as NotificationOptions);
    } else {
      // No service worker (the development server): the page's own, opening the message when clicked.
      const n = new Notification(title, options as NotificationOptions);
      n.onclick = () => {
        window.focus();
        openInApp(a.url);
        n.close();
      };
    }
    return true;
  } catch {
    return false;
  }
}

/** A voicemail heard or deleted (here or on a desk phone): its notification on this device goes. */
export async function clearNotificationsTagged(tag: string): Promise<void> {
  const reg = await registration();
  const shown = await reg?.getNotifications({ tag }).catch(() => []);
  for (const n of shown ?? []) n.close();
}

/** A conversation was read: its notifications on this device go (they're old news). */
export async function clearNotificationsFor(conversationId: string): Promise<void> {
  const reg = await registration();
  const shown = await reg?.getNotifications({ tag: `c:${conversationId}` }).catch(() => []);
  for (const n of shown ?? []) n.close();
}

/** Stitch is in front, on that conversation: it's on screen already. */
function readingNow(conversationId: string): boolean {
  return document.visibilityState === 'visible' && document.hasFocus() && window.location.pathname.endsWith(`/messages/${conversationId}`);
}

/** What a team text's sound needs to know about this page (lib/teamSoundPlayer). */
const teamEnv: AlertEnv = {
  reading: readingNow,
  quietHours: async () => inQuietHours((await currentPrefs()).quiet),
  messageSound: () => soundOn() && playMessageSound()
};

async function handle(e: LiveEvent): Promise<void> {
  const a = e.alert;
  if (!a || !e.conversationId) return;
  const visible = document.visibilityState === 'visible';
  const front = visible && document.hasFocus();
  // Reading that conversation now: it's on screen already.
  if (readingNow(e.conversationId)) return;
  // A tab on screen goes first.
  if (!visible) await new Promise((r) => window.setTimeout(r, 250));
  if (!(await claim(`stitch-alert:${e.messageId ?? `${e.conversationId}:${a.url}`}`))) return;
  if (front) {
    // A text for your ring group or queue (1.12.33): its own sound, then "New text for Billing".
    if (e.team) await teamTextAlert(e, teamEnv, true);
    else if (soundOn()) playMessageSound();
    return;
  }
  // Not in front: a notification (Stitch's sound with it, when the browser allows), unless it's quiet hours.
  const p = await currentPrefs();
  if (inQuietHours(p.quiet)) return;
  const played = e.team ? await teamTextAlert(e, teamEnv, false) : soundOn() && playMessageSound();
  await notify(a, e.conversationId, p, played, e.team);
}

/**
 * A new voicemail, in the Stitch that's open: its sound when Stitch is in front (the notice on
 * screen says who called), or a notification while it isn't. One tab does it. The push for it
 * (straight away, or a minute on for someone at Stitch) replaces the notification (same tag).
 */
export async function alertVoicemail(e: LiveEvent): Promise<void> {
  const a = e.alert;
  if (!a || !e.messageId) return;
  const visible = document.visibilityState === 'visible';
  const front = visible && document.hasFocus();
  if (!visible) await new Promise((r) => window.setTimeout(r, 250));
  if (!(await claim(`stitch-vm:${e.tenant ?? ''}:${e.mailbox ?? ''}:${e.messageId}`))) return;
  if (front) {
    if (soundOn()) playMessageSound();
    return;
  }
  const p = await currentPrefs();
  if (inQuietHours(p.quiet)) return;
  const played = soundOn() && playMessageSound();
  await notify(a, '', p, played);
}

/**
 * A missed call, in the Stitch that's open: the message sound when Stitch is in front (the notice
 * on screen says who called), or a notification while it isn't. One tab does it. A push for it
 * (to someone away from Stitch) replaces the notification (the same tag).
 */
export async function alertMissed(e: LiveEvent): Promise<void> {
  const a = e.alert;
  if (!a || !e.callId) return;
  const visible = document.visibilityState === 'visible';
  const front = visible && document.hasFocus();
  if (!visible) await new Promise((r) => window.setTimeout(r, 250));
  if (!(await claim(`stitch-missed:${e.tenant ?? ''}:${e.callId}`))) return;
  if (front) {
    if (soundOn()) playMessageSound();
    return;
  }
  const p = await currentPrefs();
  if (inQuietHours(p.quiet)) return;
  const played = soundOn() && playMessageSound();
  await notify(a, '', p, played);
}

/** For the company's people while Stitch is open: a sound and notifications for new messages. */
export function useMessageAlerts(on: boolean): void {
  useEffect(() => {
    if (!on) return;
    armSound();
    armTeamSound();
  }, [on]);
  useMessagesLive((e) => {
    if (e.type === 'alert') void handle(e);
    // A team text taken or answered (1.12.33): its reminders stop straight away.
    else if (e.type === 'conversation' && e.conversationId) void teamConversationChanged(e.conversationId);
    // Their own do not disturb and calls (anywhere): the reminders keep quiet meanwhile.
    else if (e.type === 'presence') teamPresence(e);
  }, on);
}
